import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import RowActionsMenu from '../../../components/dashboard/RowActionsMenu';
import ContactAvatar from '../../../components/contacts/ContactAvatar';
import ContactStatusChips from '../../../components/contacts/ContactStatusChips';
import ContactTemperatureChip from '../../../components/contacts/ContactTemperatureChip';
import { formatDateTime, formatPhone } from '../../../utils/formatters';

// With `getRowHeight="auto"` (the name cell has two lines), DataGrid stops
// vertically centering the other cells; this restores it (same as roles/columns.js).
function CenteredCell({ children }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
      {children}
    </Box>
  );
}

export const contactPath = (contact) => `/contacts/${contact.uuid}`;
export const contactLeadsPath = (contact) => `/leads?contact=${contact.uuid}`;

/** Items of a contact's ⋮ (grid row and card header). */
export const contactMenuActions = ({ t, navigate, contact }) => [
  {
    label: t('contacts.actions.viewDetails'),
    icon: <VisibilityOutlinedIcon fontSize="small" />,
    onClick: () => navigate(contactPath(contact)),
  },
  {
    label: t('contacts.actions.viewConversations'),
    icon: <ForumOutlinedIcon fontSize="small" />,
    onClick: () => navigate(contactLeadsPath(contact)),
  },
];

// Only the API's ordering_fields (display_name, total_conversations,
// last_message_at) are sortable.
export const buildContactColumns = ({ t, language, navigate }) => [
  {
    field: 'display_name',
    headerName: t('contacts.fields.name'),
    flex: 1,
    minWidth: 240,
    resizable: false,
    renderCell: (params) => (
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 1, minWidth: 0 }}>
        <ContactAvatar name={params.row.display_name} size={36} />
        <Stack spacing={0} sx={{ minWidth: 0 }}>
          <Link component={RouterLink} to={contactPath(params.row)} variant="body2" underline="hover" noWrap>
            {params.row.display_name || t('notAvailable')}
          </Link>
          {params.row.email && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {params.row.email}
            </Typography>
          )}
        </Stack>
      </Stack>
    ),
  },
  {
    field: 'phone_number',
    headerName: t('contacts.fields.phone'),
    width: 160,
    sortable: false,
    renderCell: (params) => (
      <CenteredCell>{params.value ? formatPhone(params.value) : t('notAvailable')}</CenteredCell>
    ),
  },
  {
    field: 'location',
    headerName: t('contacts.fields.location'),
    width: 170,
    sortable: false,
    valueGetter: (value, row) => [row.city, row.state].filter(Boolean).join(', '),
    renderCell: (params) => <CenteredCell>{params.value || t('notAvailable')}</CenteredCell>,
  },
  {
    field: 'status',
    headerName: t('contacts.fields.status'),
    width: 160,
    sortable: false,
    renderCell: (params) => (
      <CenteredCell>
        <ContactStatusChips contact={params.row} />
      </CenteredCell>
    ),
  },
  {
    field: 'temperature',
    headerName: t('contacts.fields.temperature'),
    width: 120,
    sortable: false,
    valueGetter: (value, row) => row.ai_insights?.last_temperature ?? null,
    renderCell: (params) => (
      <CenteredCell>
        <ContactTemperatureChip value={params.value} />
      </CenteredCell>
    ),
  },
  {
    field: 'total_conversations',
    headerName: t('contacts.fields.conversations'),
    width: 110,
    type: 'number',
    align: 'left',
    headerAlign: 'left',
    renderCell: (params) => <CenteredCell>{params.value ?? 0}</CenteredCell>,
  },
  {
    field: 'last_message_at',
    headerName: t('contacts.fields.lastMessage'),
    width: 160,
    renderCell: (params) => (
      <CenteredCell>{formatDateTime(params.value, language, { empty: t('notAvailable') })}</CenteredCell>
    ),
  },
  {
    field: 'actions',
    headerName: '',
    width: 56,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    hideable: false,
    renderCell: (params) => (
      <CenteredCell>
        <RowActionsMenu
          ariaLabel={t('contacts.actions.menu')}
          actions={contactMenuActions({ t, navigate, contact: params.row })}
        />
      </CenteredCell>
    ),
  },
];
